/**
 * @typedef {Object} ExpirationDefaults
 * @property {boolean} showExpiration
 * @property {'Expiration' | 'Production date'} label
 * @property {string} defaultDate
 * @property {boolean} isLocked
 * @property {boolean} isRequired
 */

/**
 * @param {unknown} expiresOn
 * @returns {string}
 */
function normalizeExpiresOn(expiresOn) {
    if (expiresOn == null || expiresOn === '') {
        return '';
    }

    if (typeof expiresOn === 'object' && expiresOn !== null && 'date' in expiresOn) {
        return String(expiresOn.date).slice(0, 10);
    }

    return String(expiresOn).slice(0, 10);
}

/**
 * @param {{ original?: object } | object | null | undefined} rowData
 * @param {string | null | undefined} poType
 * @returns {ExpirationDefaults}
 */
function resolveExpirationDefaults(rowData, poType) {
    const hidden = {
        showExpiration: false,
        label: /** @type {const} */ ('Expiration'),
        defaultDate: '',
        isLocked: false,
        isRequired: false,
    };

    if (poType === 'PACKAGING') {
        return hidden;
    }

    const original = rowData?.original ?? rowData ?? {};
    const { expirationType } = original;
    const defaultDate = normalizeExpiresOn(original.expiresOn);

    if (expirationType === 'DAYS_AFTER_RECEIPT' || expirationType === 'NO_EXPIRATION_DATE') {
        return {
            showExpiration: true,
            label: /** @type {const} */ ('Expiration'),
            defaultDate,
            isLocked: true,
            isRequired: false,
        };
    }

    if (expirationType === 'EXPIRATION_DATE' || expirationType === 'PRODUCTION_DATE') {
        return {
            showExpiration: true,
            label: expirationType === 'PRODUCTION_DATE' ? 'Production date' : 'Expiration',
            defaultDate,
            isLocked: defaultDate !== '',
            isRequired: true,
        };
    }

    return {
        showExpiration: true,
        label: /** @type {const} */ ('Expiration'),
        defaultDate,
        isLocked: false,
        isRequired: false,
    };
}

/**
 * @param {ExpirationDefaults | null | undefined} expirationDefaults
 * @param {string} [lineExpiration]
 * @returns {{ showExpiration: boolean, label: string, isLocked: boolean, value: string }}
 */
function getExpirationFieldState(expirationDefaults, lineExpiration = '') {
    const showExpiration = expirationDefaults?.showExpiration === true;
    const isLocked = expirationDefaults?.isLocked === true;

    return {
        showExpiration,
        label: expirationDefaults?.label ?? 'Expiration',
        isLocked,
        value: isLocked ? (expirationDefaults?.defaultDate ?? '') : lineExpiration,
    };
}

/**
 * @param {Array<{ lines: Array<{ numCases: number, expiration: string }> }>} pallets
 * @param {ExpirationDefaults} expirationDefaults
 * @returns {string | null}
 */
function getExpirationValidationError(pallets, expirationDefaults) {
    if (!expirationDefaults.isRequired) {
        return null;
    }

    const hasMissingExpiration = pallets.some((pallet) =>
        pallet.lines.some((line) => line.numCases > 0 && !line.expiration),
    );

    if (!hasMissingExpiration) {
        return null;
    }

    return expirationDefaults.label === 'Production date'
        ? 'Production date cannot be blank'
        : 'Expiration date cannot be blank';
}

/**
 * @param {Array<{ lines: Array<{ numCases: number }> }>} pallets
 * @returns {string | null}
 */
function getCaseQtyValidationError(pallets) {
    if (pallets.length === 0) {
        return null;
    }

    const hasCases = pallets.some((pallet) => pallet.lines.some((line) => line.numCases > 0));

    return hasCases ? null : 'Cases on pallet must be greater than 0';
}

export {
    getCaseQtyValidationError,
    getExpirationFieldState,
    getExpirationValidationError,
    normalizeExpiresOn,
    resolveExpirationDefaults,
};
